/**
 * ForkHire Responsive + RTL Design System Utilities
 *
 * Mirrors the ForkSeek responsive system (src/lib/responsive.ts) for the
 * employer portal.  Shared constants ensure consistent container widths,
 * grid densities, spacing, and typography scaling across every breakpoint
 * from 320 px mobile to 4K ultra-wide.
 *
 * Breakpoint strategy (same as ForkSeek):
 *   xs:  360        (standard mobile)
 *   sm:  640        (Tailwind default)
 *   md:  768
 *   lg:  1024
 *   xl:  1280
 *   2xl: 1536
 *   3xl: 1920       (large desktop / ultra-wide)
 *   4xl: 2560       (4K)
 */

/* ─── Page containers ─── */

/** Standard page container — scales up on ultra-wide / 4K */
export const HIRE_PAGE_CONTAINER =
  'max-w-[1200px] 3xl:max-w-[1440px] 4xl:max-w-[1600px] mx-auto w-full';

/** Narrow container for settings / checkout / forms */
export const HIRE_NARROW_CONTAINER =
  'max-w-[600px] lg:max-w-[700px] mx-auto w-full';

/** Medium container (billing, org-profile) */
export const HIRE_MEDIUM_CONTAINER =
  'max-w-[900px] 3xl:max-w-[1000px] mx-auto w-full';

/* ─── Spacing ─── */

/** Consistent vertical section spacing */
export const HIRE_SECTION_SPACING = 'space-y-3 sm:space-y-6 lg:space-y-8';

/* ─── Typography ─── */

/** Page title classes — responsive font sizing + font-display */
export const HIRE_PAGE_TITLE =
  'min-w-0 whitespace-normal break-words [overflow-wrap:anywhere] text-xl sm:text-2xl lg:text-3xl font-bold text-foreground font-display';

/* ─── Page header card ─── */

/** Card padding inside page header cards */
export const HIRE_PAGE_HEADER_PAD =
  'p-5 sm:p-6 lg:p-8 3xl:p-10';

/**
 * List-management header cards must stay in normal document flow.
 * Making the full KPI/search header sticky causes desktop rows to scroll
 * under the toolbar and become partially hidden.
 */
export const HIRE_LIST_HEADER_FLOW = 'relative z-10';

/* ─── Stat / KPI grids ─── */

/** 2→4 stat grid */
export const HIRE_STAT_GRID =
  'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4';

/** Stat card padding */
export const HIRE_STAT_PAD = 'p-4 sm:p-5';

/* ─── Card grids ─── */

/** Generic card grid (jobs grid view, notifications, etc.) */
export const HIRE_CARD_GRID =
  'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 gap-4 sm:gap-5';

/* ─── Dashboard split layout ─── */

/** Two-column dashboard: main + sidebar */
export const HIRE_DASHBOARD_GRID =
  'grid grid-cols-1 lg:grid-cols-5 gap-3 sm:gap-5 lg:gap-6 3xl:gap-8';

/* ─── Sticky header offset ─── */

/**
 * Sticky header `top` value that accounts for AppLayout main padding.
 * The main element uses scaled padding (p-4 → p-20), so sticky children
 * must match.
 */
export const HIRE_STICKY_TOP =
  'top-0';

/* ─── RTL flip ─── */

/** Directional icon flip class (already in index.css) */
export const RTL_FLIP = 'rtl-flip';
